import { HotelsList } from '@/components/hotels';
import { hotelCrudConfig, type Hotel } from '@/config/objects/hotels';
import {
  promotionalOfferCrudConfig,
  type PromotionalOffer,
} from '@/config/objects/promotional-offers';
import {
  CrudDetailView,
  PageContainer,
  useApiClient,
  type CrudConfig,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Promotional Offer detail page
 */
export function PromotionalOfferDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [promotionalOffer, setPromotionalOffer] = React.useState<PromotionalOffer | null>(null);

  /**
   * Delete promotional offer mutation
   */
  const deleteMutation = useMutation({
    mutationFn: async (
      promotionalOfferId: string,
    ): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/promotional-offers/${promotionalOfferId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        // Invalidate promotional offers list to refresh data
        queryClient.invalidateQueries({ queryKey: ['PromotionalOffer'] });
        navigate('/promotional-offers');
      } else {
        throw new Error(response.error || 'Failed to delete promotional offer');
      }
    },
  });

  // Create custom config for promotional offer-specific hotels
  const promotionalOfferHotelsConfig: CrudConfig<Hotel> = useMemo(
    () => ({
      ...hotelCrudConfig,
      entityName: 'Hotel',
      entityNamePlural: 'Hotels',
      endpoints: {
        ...hotelCrudConfig.endpoints,
        list: `/promotional-offers/${id}/hotels`,
        delete: (hotelId: string) => `/promotional-offers/${id}/hotels/${hotelId}`,
      },
      listView: {
        ...hotelCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/hotels',
            // Exclude hotels already in this promotional offer
            where: promotionalOffer
              ? {
                  promotionalOffers: {
                    none: {
                      promotionalOfferId: promotionalOffer.id,
                    },
                  },
                }
              : undefined,
            onAdd: async (hotel: Hotel) => {
              await apiClient.post(`/promotional-offers/${id}/hotels`, { hotelId: hotel.id });
              toast.success('Hotel added to promotional offer');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (hotel: Hotel) => {
              await apiClient.delete(`/promotional-offers/${id}/hotels/${hotel.id}`);
              toast.success('Hotel removed from promotional offer');
            },
          },
        },
      },
    }),
    [promotionalOffer, id, apiClient],
  );

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Promotional Offer Not Found</h2>
          <p className="text-muted-foreground">The requested promotional offer could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  /**
   * Navigate to edit form for this promotional offer
   */
  const handleEdit = (promotionalOffer: PromotionalOffer) => {
    navigate(`/promotional-offers/${promotionalOffer.id}/edit`);
  };

  /**
   * Handle promotional offer deletion
   */
  const handleDelete = async (promotionalOffer: PromotionalOffer) => {
    return toast.promise(deleteMutation.mutateAsync(promotionalOffer.id), {
      loading: 'Deleting promotional offer...',
      success: 'Promotional offer deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete promotional offer',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Promotional Offers', href: '/promotional-offers' },
    { label: promotionalOffer?.name || 'Promotional Offer', current: true },
  ];

  return (
    <PageContainer>
      <div className="space-y-6">
        <CrudDetailView
          config={promotionalOfferCrudConfig}
          itemId={id}
          breadcrumbs={breadcrumbs}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onData={(promotionalOfferData: PromotionalOffer) =>
            setPromotionalOffer(promotionalOfferData)
          }
        />

        {/* Promotional Offer Hotels Section */}
        <HotelsList config={promotionalOfferHotelsConfig} className="p-0" baseUrl="/hotels" />
      </div>
    </PageContainer>
  );
}
